import React, { useState, useEffect, useRef } from 'react';

import { Dayjs } from 'dayjs';

import { saveAs } from 'file-saver';

import { useResizeObserver } from 'usehooks-ts';

import { Table, Form, Switch, DatePicker, notification, Tooltip, Button, Flex, Modal } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, EyeFilled, QuestionCircleOutlined, DownloadOutlined } from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';

import { delayAction, getDateTimeLocal, dataURItoBlob } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flc/server';

import { INotification } from '@entities/notification';
import { INotificationFilter } from '@entities/notification-filter';
import { NotificationMessageType } from '@enums/notification-message-type';

const dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc');
dayjs.extend(utc);

const filterContext: string = 'Notifications';

const Notifications = () => {
    const initFilter: INotificationFilter = {
        personalOnly: true,
        unreadOnly: true,
        messageType: NotificationMessageType.Push,
    };

    const d = useAppDispatch();

    const { RangePicker } = DatePicker;
    const [modal, modalCntextHolder] = Modal.useModal();

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();

    const [entities, setEntities] = useState<Array<INotification>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    const filter = useAppSelector<INotificationFilter>((s) => s.filters[filterContext]);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setEntities([]);

        setLoading(true);

        serverFetch('notifications', { method: 'GET', queryParams: filter })
            .then((data) => {
                setEntities(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения сообщений', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onSetReadAll = () => {
        serverFetch(`notifications/readall`, { method: 'POST' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка изменения статуса сообщения', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDownload = async (file: any) => {
        serverFetch(`notifications/attachment/${file?.uid}/download`, { method: 'GET' })
            .then((data) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex) => {
                exception(api, 'Ошибка загрузки документа', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => setRefreshRequired(true),
                    },
                    {
                        label: 'Прочитать все',
                        key: 'setReadAll',
                        showLabel: true,
                        disabled: loading,
                        onClick: () => {
                            modal.confirm({
                                title: 'Пометить все сообщения как прочитанные?',
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onSetReadAll();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <RangePicker
                            style={{ width: 260 }}
                            allowEmpty={[true, true]}
                            key='created'
                            format='DD.MM.YYYY'
                            placeholder={['Отправлено с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.createdFrom ? dayjs(filter?.createdFrom) : null,
                                filter?.createdTo ? dayjs(filter?.createdTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                createdFrom: undefined,
                                                createdTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            createdFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            createdTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                        <Form.Item key='unreadOnly' label='Непрочитанные' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.unreadOnly}
                                onChange={(value) => {
                                    d(setFilter({ ...filter, unreadOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<INotification> = [
            {
                title: '',
                align: 'center',
                width: 35,
                render: (_, record) => {
                    return (
                        record.isRead && (
                            <Tooltip title='Прочитано'>
                                <EyeFilled style={{ color: 'var(--primary-color)' }} />
                            </Tooltip>
                        )
                    );
                },
            },
            {
                title: 'Сообщение',
                width: 700,
                onCell: () => ({
                    style: {
                        whiteSpace: 'pre-wrap',
                    },
                }),
                render: (_: any, record: INotification) => {
                    return (
                        <>
                            <div style={{ fontWeight: 600, marginBottom: 5 }}>{record.subject}</div>
                            <div>{record.body}</div>
                            {record.files && record.files.length > 0 && (
                                <Flex gap='small' orientation='vertical' align='start' style={{ marginTop: 10 }}>
                                    {record.files.map((f) => (
                                        <Button
                                            style={{ paddingLeft: 0 }}
                                            icon={<DownloadOutlined />}
                                            variant='link'
                                            color='blue'
                                            onClick={() => onDownload(f)}
                                        >
                                            {f.name}
                                        </Button>
                                    ))}
                                </Flex>
                            )}
                        </>
                    );
                },
            },
            {
                title: 'Отправлено',
                width: 130,
                render: (_: any, record: INotification) => {
                    return getDateTimeLocal(record.createdOn);
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={entities}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 190px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {contextHolder}
            {modalCntextHolder}
        </div>
    );
};

export default Notifications;
